import { useCallback, useState } from 'react'
import { MoreHorizontal, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  usePersonalNoteContent,
  usePersonalNoteContentMutations,
  usePersonalNoteMutations,
} from '@/lib/db/rxdb/hooks'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { cn } from '@/lib/utils'

interface NoteRowOptionsMenuProps {
  noteId: string
  noteTitle: string
  className?: string
  onDeleted?: () => void
}

export function NoteRowOptionsMenu({
  noteId,
  noteTitle,
  className,
  onDeleted,
}: NoteRowOptionsMenuProps) {
  const { t } = useTranslation('notes')
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { deletePersonalNote } = usePersonalNoteMutations()
  const { deletePersonalNoteContent } = usePersonalNoteContentMutations()
  const { content } = usePersonalNoteContent(noteId)

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true)
      if (content?.id) {
        await deletePersonalNoteContent(content.id)
      }
      await deletePersonalNote(noteId)
      setIsDeleteAlertOpen(false)
      onDeleted?.()
    } catch (error) {
      logger.logError(error as Error, LogCategory.DATABASE, {
        component: 'NoteRowOptionsMenu',
        noteId,
      })
    } finally {
      setIsDeleting(false)
    }
  }, [
    noteId,
    content?.id,
    deletePersonalNote,
    deletePersonalNoteContent,
    onDeleted,
  ])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center justify-center rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
              className,
            )}
            aria-label={t('moreOptions')}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            variant="destructive"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsDeleteAlertOpen(true)
            }}
          >
            <Trash2 className="size-4" />
            {t('deleteNote')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader className="overflow-hidden">
            <AlertDialogTitle className="truncate">
              {t('deleteNoteTitle', { title: noteTitle })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteNoteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault()
                e.stopPropagation()
                await handleDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('deleting') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
